import type { ErrorMessage } from '@repo/types/messages/error';
import type { NotificationPublic, ReadNotificationsType } from '@repo/types/notifications';

import { InfoIcon } from 'lucide-react';

import NotificationCard from '@/components/features/notifications/NotificationCard';
import NotificationCardSkeleton from '@/components/features/notifications/NotificationCardSkeleton';
import NotificationsEmpty from '@/components/features/notifications/NotificationsEmpty';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { InfiniteScroll } from '@/components/ui/infinite-scroll';

interface NotificationsListProps {
  notifications: NotificationPublic[];
  isEmpty: boolean;
  hasNextPage: boolean;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  errorMessage: ErrorMessage;
  next: () => void;
  makeNotificationsRead: (data: ReadNotificationsType[]) => void;
}

export default function NotificationsList(notificationsListProps: NotificationsListProps) {
  const { notifications, isEmpty, next, hasNextPage, isFetching, isLoading, isError, errorMessage, makeNotificationsRead } = notificationsListProps;

  return (
    <div className={'shrink grow space-y-2 overflow-y-auto p-4'}>
      <Alert
        variant={'destructive'}
        data-visible={isError}
        className={
          'overflow-hidden opacity-100 transition-[height,opacity] duration-400 data-[visible=false]:max-h-0 data-[visible=false]:p-0 data-[visible=false]:opacity-0 data-[visible=true]:h-fit data-[visible=true]:opacity-100'
        }
      >
        <InfoIcon />
        <AlertTitle>{errorMessage.title}</AlertTitle>
        <AlertDescription>{errorMessage.detail}</AlertDescription>
      </Alert>
      {isEmpty && !isLoading && !isError ? <NotificationsEmpty /> : null}
      {notifications.map((notification) => (
        <NotificationCard key={notification.id} makeRead={(data) => makeNotificationsRead([data])} notification={notification} />
      ))}

      {isLoading && (
        <>
          <NotificationCardSkeleton />
          <NotificationCardSkeleton />
          <NotificationCardSkeleton />
        </>
      )}
      <InfiniteScroll hasNextPage={hasNextPage} isLoading={isFetching} isFetching={isFetching} next={next} threshold={0.65} root={null}>
        {hasNextPage && !isError && <NotificationCardSkeleton />}
      </InfiniteScroll>
    </div>
  );
}
